import AddIcon from "@mui/icons-material/Add";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MinusIcon from "@mui/icons-material/Remove";
import SecurityIcon from "@mui/icons-material/Security";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import RowActions from "_shared/_views/tables/row-actions";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { principalChanged } from "users/__session/principal-change";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import AuthEmailAPIService, { AuthResponse } from "users/_api/auth-email-api-service";
import UserInformation from "users/information/user-information";
import OnboardingWorkResolution from "users/super-users/_views/user-administration/onboarding-work-resolution";
import SearchUsersAPIRequest from "users/super-users/_api/commands/search-users-api-request";
import {
  useLoginAsUserMutation,
  useRegisterSuperUserMutation,
  useSearchUsersQuery,
  useUnregisterSuperUserMutation,
} from "users/super-users/_api/super-user-api";

type UsersAdminProps = {
  onProfileView: (type: string, id?: string) => void;
};

const columnHelper = createMRTColumnHelper<UserInformation>();

export function getUserDisplayName(user: UserInformation): string {
  return user.name.fullName.trim() || (user.email?.value ?? user.id.value);
}

export default function UsersAdmin(props: Readonly<UsersAdminProps>) {
  const { onProfileView } = props;
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  const confirm = useConfirmDialog();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  const navigate = useNavigate();
  const searchRequest = useMemo(
    () =>
      new SearchUsersAPIRequest(
        query ?? "",
        pagination.pageIndex,
        pagination.pageSize,
        sorting.length > 0 ? sorting[0] : undefined,
      ),
    [query, pagination.pageIndex, pagination.pageSize, sorting],
  );
  const usersQuery = useSearchUsersQuery({ request: searchRequest });
  const [registerSuperUserMutation] = useRegisterSuperUserMutation();
  const [unregisterSuperUser] = useUnregisterSuperUserMutation();
  const [loginAsSelectedUser] = useLoginAsUserMutation();
  const data = usersQuery.data?.data ?? [];
  const rowCount = usersQuery.data?.totalElements ?? 0;

  const refreshUsers = useCallback(async () => {
    await usersQuery.refetch();
  }, [usersQuery]);

  async function registerSuperUser(user: UserInformation) {
    if (!user) return;
    const response = await confirm({
      title: `Register ${getUserDisplayName(user)} as a superuser?`,
      message: "Are you sure you want to register this user as a superuser?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await registerSuperUserMutation({ userId: user.id }).unwrap();
      await refreshUsers();
    } catch {
      enqueueSnackbar("Couldn't register superuser", { variant: "error" });
    }
  }

  async function unRegisterSuperUser(user: UserInformation) {
    const response = await confirm({
      title: `Unregister ${getUserDisplayName(user)} as a superuser?`,
      message: "Are you sure you want to unregister this user as a superuser?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await unregisterSuperUser({ userId: user.id }).unwrap();
      await refreshUsers();
    } catch {
      enqueueSnackbar("Couldn't unregister superuser", { variant: "error" });
    }
  }

  async function resetUserPassword(data: UserInformation | UserInformation[]) {
    let user: UserInformation;
    if (Array.isArray(data)) {
      user = data[0];
    } else {
      user = data;
    }
    const response = await confirm({
      title: `Reset ${getUserDisplayName(user)}'s password?`,
      message: "Are you sure you want to reset this user's password?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await AuthEmailAPIService.requestPasswordReset(user.email!);
      enqueueSnackbar("Sent password reset email to user", { variant: "success" });
    } catch {
      enqueueSnackbar("Couldn't reset user password", { variant: "error" });
    }
  }

  async function loginAsUser(data: UserInformation | UserInformation[]) {
    let user: UserInformation;
    if (Array.isArray(data)) {
      user = data[0];
    } else {
      user = data;
    }
    const userId = user.id;
    const response = await confirm({
      title: `Login as ${getUserDisplayName(user)}?`,
      message: "Are you sure you want to login as this user?",
      okButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const authResponse: AuthResponse = await loginAsSelectedUser({ userId }).unwrap();
      session.initializeFromAuthResponse(authResponse);
      dispatch(principalChanged());
      navigate("/dashboard", { replace: true });
    } catch {
      enqueueSnackbar("Couldn't login as user", { variant: "error" });
    }
  }

  const renderCompanyColumn = useCallback(
    (user: UserInformation) => {
      if (!user.companyId) return null;
      return (
        <Link
          style={{
            color: "#000",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() => {
            onProfileView("company", user?.companyId?.value);
          }}
        >
          {user.companyName}
        </Link>
      );
    },
    [onProfileView],
  );

  const renderEmailCell = useCallback((email: string) => {
    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: "500",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {email}
      </Typography>
    );
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor((user) => user.email!.value, {
        header: "Email",
        id: "email",
        Cell: ({ row }) => renderEmailCell(row.original.email!.value),
      }),
      columnHelper.accessor((user) => user.name.firstName, {
        header: "First Name",
        id: "firstName",
      }),
      columnHelper.accessor((user) => user.name.lastName, {
        header: "Last Name",
        id: "lastName",
      }),
      columnHelper.accessor(renderCompanyColumn, {
        header: "Company",
        id: "companyName",
      }),
      columnHelper.accessor((user) => user.onboardingWork?.state ?? "", {
        header: "Onboarding",
        id: "onboardingState",
        Cell: ({ row }) => row.original.onboardingWork
          ? <OnboardingWorkResolution onboardingWork={row.original.onboardingWork} onChanged={() => void refreshUsers()} />
          : <Typography variant="body2" color="text.secondary">—</Typography>,
      }),
    ],
    [refreshUsers, renderCompanyColumn, renderEmailCell],
  );

  const tableOptions: MRT_TableOptions<UserInformation> = {
    columns,
    data,
    enableRowSelection: true,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    enableGlobalFilter: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      showGlobalFilter: true,
    },
    muiExpandButtonProps: ({ row }) => ({
      children: row.getIsExpanded() ? <MinusIcon /> : <AddIcon />,
    }),
    renderRowActions: ({ row }) => {
      const user = row.original;
      const userName = getUserDisplayName(user);
      return (
        <RowActions
          ariaLabel={`Actions for ${userName}`}
          actions={[
            user.isSuperUser
              ? {
                  key: "superuser",
                  label: "Unregister As Superuser",
                  ariaLabel: `Unregister ${userName} as superuser`,
                  icon: <SecurityIcon />,
                  destructive: true,
                  onClick: () => {
                    void unRegisterSuperUser(user);
                  },
                }
              : {
                  key: "superuser",
                  label: "Register As Superuser",
                  ariaLabel: `Register ${userName} as superuser`,
                  icon: <SecurityIcon />,
                  onClick: () => {
                    void registerSuperUser(user);
                  },
                },
            {
              key: "reset-password",
              label: "Force Password Reset",
              ariaLabel: `Force password reset for ${userName}`,
              icon: <VpnKeyIcon />,
              onClick: () => {
                void resetUserPassword(user);
              },
            },
            {
              key: "login-as",
              label: "Login As User",
              ariaLabel: `Login as ${userName}`,
              icon: <LockOpenIcon />,
              onClick: () => {
                void loginAsUser(user);
              },
            },
          ]}
        />
      );
    },
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    rowCount: rowCount,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    muiTableContainerProps: { sx: { flex: 1 } },
    muiTablePaperProps: { sx: { height: "100%", display: "flex", flexDirection: "column" } },
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: usersQuery.isError,
      isLoading: usersQuery.isFetching,
      onRetry: () => void usersQuery.refetch(),
      subject: "users",
    }),
    muiToolbarAlertBannerProps: usersQuery.isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading: usersQuery.isLoading,
      pagination,
      showAlertBanner: usersQuery.isError,
      showProgressBars: usersQuery.isFetching,
      sorting,
    },
  };

  return <MRTNoMemo<UserInformation> options={tableOptions} />;
}
